/**
 * @fileoverview Form library picker for the template builder — browse and select reusable forms.
 * @module ui-admin/components/template-builder/FormLibraryPicker
 *
 * Responsibilities:
 * - Display a searchable, filterable list of reusable forms from the form library.
 * - Support selection of forms for insertion into template builder sections.
 * - Categorize forms by type (reusable, layout, document).
 *
 * Dependencies: templateTypes, FormLibraryService interface.
 * Architecture: Presentation component — receives form data via service prop.
 * Notes: Shared across tenant-web and system-web template builders.
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, X, BookMarked, LayoutDashboard, FileText, ChevronRight, Star } from 'lucide-react';
import type { TemplateBuilderForm, TemplateType } from '../../types/templateTypes';

export interface FormLibraryService {
  listForms(params?: { reusable?: boolean; templateType?: TemplateType; limit?: number }): Promise<{ items: TemplateBuilderForm[] }>;
  toggleFormFavorite(formId: string): Promise<TemplateBuilderForm | null>;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FormLibraryPickerProps {
  templateType?: TemplateType;
  onSelect: (form: TemplateBuilderForm) => void;
  onClose: () => void;
  formService: FormLibraryService;
}

// ---------------------------------------------------------------------------
// FormLibraryPicker
// ---------------------------------------------------------------------------

export function FormLibraryPicker({ templateType, onSelect, onClose, formService }: FormLibraryPickerProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [libraryForms, setLibraryForms] = useState<TemplateBuilderForm[]>([]);
  const [loading, setLoading] = useState(true);

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const result = await formService.listForms({ reusable: true, templateType, limit: 200 });
      setLibraryForms(result.items);
    } finally {
      setLoading(false);
    }
  }, [formService, templateType]);

  useEffect(() => { loadForms(); }, [loadForms]);

  const handleToggleFavorite = useCallback(async (formId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await formService.toggleFormFavorite(formId);
    await loadForms();
    if (selectedId === formId) setSelectedId(null);
  }, [loadForms, selectedId]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(libraryForms.map((f) => f.category || 'Other')));
    return cats.sort();
  }, [libraryForms]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return libraryForms;
    return libraryForms.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        (f.description || '').toLowerCase().includes(q) ||
        (f.category || '').toLowerCase().includes(q),
    );
  }, [search, libraryForms]);

  const selectedForm = libraryForms.find((f) => f._id === selectedId) ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-[780px] max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <BookMarked size={18} className="text-purple-600 shrink-0" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-900">Form Library</h2>
            <p className="text-xs text-gray-500">Choose a shared form to add to this template</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search forms…"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-purple-300 bg-gray-50"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={11} />
              </button>
            )}
          </div>
        </div>

        {/* Body — two-pane */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left: form list */}
          <div className="w-64 border-r border-gray-100 overflow-y-auto shrink-0">
            {loading ? (
              <div className="px-4 py-8 text-xs text-gray-400 text-center">Loading…</div>
            ) : libraryForms.length === 0 ? (
              <div className="px-4 py-8 text-xs text-gray-400 text-center flex flex-col items-center gap-2">
                <Star size={20} className="text-gray-200" />
                <p>No favorites yet.</p>
                <p className="text-[10px] text-gray-300">Star a form on the canvas to add it here.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-8 text-xs text-gray-400 text-center">No forms match your search.</div>
            ) : search ? (
              <div className="py-1">
                {filtered.map((form) => (
                  <FormListItem
                    key={form._id}
                    form={form}
                    isSelected={selectedId === form._id}
                    onClick={() => setSelectedId(form._id)}
                    onUnfavorite={(e) => handleToggleFavorite(form._id, e)}
                  />
                ))}
              </div>
            ) : (
              categories.map((cat) => {
                const catForms = filtered.filter((f) => (f.category || 'Other') === cat);
                if (catForms.length === 0) return null;
                return (
                  <div key={cat}>
                    <div className="px-4 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                      {cat}
                    </div>
                    {catForms.map((form) => (
                      <FormListItem
                        key={form._id}
                        form={form}
                        isSelected={selectedId === form._id}
                        onClick={() => setSelectedId(form._id)}
                        onUnfavorite={(e) => handleToggleFavorite(form._id, e)}
                      />
                    ))}
                  </div>
                );
              })
            )}
          </div>

          {/* Right: preview pane */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedForm ? (
              <FormPreviewPane form={selectedForm} />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                  <BookMarked size={22} className="text-purple-300" />
                </div>
                <p className="text-xs text-gray-400">Select a form on the left to preview it</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-400">{loading ? 'Loading…' : `${filtered.length} favorite form${filtered.length !== 1 ? 's' : ''}`}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={!selectedForm}
              onClick={() => selectedForm && onSelect(selectedForm)}
              className="px-4 py-1.5 text-xs font-medium text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-purple-600 hover:bg-purple-700"
            >
              Add to Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormListItem
// ---------------------------------------------------------------------------

function FormListItem({ form, isSelected, onClick, onUnfavorite }: {
  form: TemplateBuilderForm;
  isSelected: boolean;
  onClick: () => void;
  onUnfavorite?: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`group w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer transition-colors ${
        isSelected ? 'bg-purple-50 border-r-2 border-purple-500' : 'hover:bg-gray-50 border-r-2 border-transparent'
      }`}
    >
      <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
        <Star size={13} className="text-amber-400" fill="currentColor" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-gray-800 truncate">{form.name}</div>
        {form.description && (
          <div className="text-[10px] text-gray-400 truncate mt-0.5">{form.description}</div>
        )}
      </div>
      {isSelected
        ? <ChevronRight size={12} className="text-purple-500 shrink-0" />
        : onUnfavorite && (
            <button
              onClick={onUnfavorite}
              title="Remove from favorites"
              className="invisible group-hover:visible p-0.5 text-gray-300 hover:text-amber-500 transition-colors shrink-0 rounded-sm"
            >
              <X size={11} />
            </button>
          )
      }
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormPreviewPane — tabbed: Info | Preview
// ---------------------------------------------------------------------------

type PreviewTab = 'info' | 'preview';

function FormPreviewPane({ form }: { form: TemplateBuilderForm }) {
  const [tab, setTab] = useState<PreviewTab>('info');

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex gap-1 mb-4 border-b border-gray-100 pb-0">
        {(['info', 'preview'] as PreviewTab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-purple-500 text-purple-700 bg-purple-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t === 'info' ? 'Information' : 'Preview'}
          </button>
        ))}
      </div>

      {/* Info tab */}
      {tab === 'info' && (
        <div className="space-y-5 flex-1 overflow-y-auto">
          {/* Title */}
          <div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                <BookMarked size={18} className="text-purple-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{form.name}</h3>
                {form.category && (
                  <span className="text-[10px] text-gray-400 mt-0.5 block">{form.category}</span>
                )}
              </div>
            </div>
            {form.description && (
              <p className="mt-3 text-xs text-gray-600 leading-relaxed">{form.description}</p>
            )}
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            <MetaRow label="Fields" value={String(form.fields.length)} />
            <MetaRow label="Sections" value={String(form.layout.sections.length)} />
          </div>

          {/* Sections list */}
          {form.layout.sections.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Sections</p>
              <div className="space-y-1">
                {form.layout.sections.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg text-xs text-gray-700">
                    <LayoutDashboard size={11} className="text-gray-400 shrink-0" />
                    {s.title || `Section ${s.order + 1}`}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Library notice */}
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
            This is a shared form template from the library. When added, it becomes part of your template and can be customized independently.
          </div>
        </div>
      )}

      {/* Preview tab */}
      {tab === 'preview' && (
        <div className="flex-1 overflow-y-auto">
          <FormVisualPreview form={form} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormVisualPreview — renders a read-only visual mock of the form layout
// ---------------------------------------------------------------------------

const FIELD_TYPE_PREVIEW_LABELS: Record<string, string> = {
  toggle: 'Toggle',
  text: 'Text Input',
  textarea: 'Text Area',
  number: 'Number',
  measurement: 'Measurement',
  dropdown: 'Dropdown',
  checkboxGroup: 'Checkbox Group',
  radioGroup: 'Radio Group',
  camera: 'Camera / Photo',
  date: 'Date',
  dateTime: 'Date & Time',
  photo: 'Photo',
  signature: 'Signature',
  location: 'Location',
  section: 'Section Header',
  instructions: 'General Information',
  repeatableList: 'Repeatable List',
  dataBound: 'Data Bound',
  inspectors: 'Inspectors',
};

function FormVisualPreview({ form }: { form: TemplateBuilderForm }) {
  const hasContent = form.layout.sections.length > 0 || form.fields.length > 0;

  if (!hasContent) {
    return (
      <div className="space-y-3">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Form Layout Preview</p>
        {/* Illustrative skeleton — shows what the form will look like once fields are configured */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
            <LayoutDashboard size={11} className="text-gray-300 shrink-0" />
            <div className="h-2.5 w-24 bg-gray-200 rounded-sm animate-pulse" />
          </div>
          <div className="p-2 space-y-1.5">
            {[60, 80, 45, 70].map((w, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm bg-gray-50 border border-gray-100">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-200 shrink-0" />
                <div className={`h-2 bg-gray-200 rounded-sm animate-pulse`} style={{ width: `${w}%` }} />
                <div className="h-2 w-10 bg-gray-100 rounded-sm animate-pulse shrink-0" />
              </div>
            ))}
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
            <LayoutDashboard size={11} className="text-gray-300 shrink-0" />
            <div className="h-2.5 w-16 bg-gray-200 rounded-sm animate-pulse" />
          </div>
          <div className="p-2 space-y-1.5">
            {[50, 65].map((w, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm bg-gray-50 border border-gray-100">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-200 shrink-0" />
                <div className={`h-2 bg-gray-200 rounded-sm animate-pulse`} style={{ width: `${w}%` }} />
                <div className="h-2 w-10 bg-gray-100 rounded-sm animate-pulse shrink-0" />
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-gray-400 text-center pt-1">
          Field layout is defined when this form is configured in your template.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Form Layout</p>
      {form.layout.sections.map((section) => {
        const sectionFields = section.fieldIds
          .map((fid) => form.fields.find((f) => f.id === fid))
          .filter(Boolean) as typeof form.fields;
        return (
          <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {section.title && (
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                <LayoutDashboard size={11} className="text-gray-400 shrink-0" />
                <span className="text-xs font-semibold text-gray-700">{section.title}</span>
              </div>
            )}
            <div className="p-2 space-y-1">
              {sectionFields.length === 0 ? (
                <div className="px-2 py-1.5 text-[10px] text-gray-400 italic">No fields in this section</div>
              ) : (
                sectionFields.map((field) => (
                  <div key={field.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm bg-gray-50 border border-gray-100">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-300 shrink-0" />
                    <span className="text-xs text-gray-700 flex-1 truncate">{field.label}</span>
                    <span className="text-[9px] text-gray-400 shrink-0">{FIELD_TYPE_PREVIEW_LABELS[field.type] ?? field.type}</span>
                    {field.required && <span className="text-[9px] text-red-400 shrink-0">Required</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
      {/* Fields not in any section */}
      {form.fields.filter((f) => !form.layout.sections.some((s) => s.fieldIds.includes(f.id))).map((field) => (
        <div key={field.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-300 shrink-0" />
          <span className="text-xs text-gray-700 flex-1 truncate">{field.label}</span>
          <span className="text-[9px] text-gray-400 shrink-0">{FIELD_TYPE_PREVIEW_LABELS[field.type] ?? field.type}</span>
          {field.required && <span className="text-[9px] text-red-400 shrink-0">Required</span>}
        </div>
      ))}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-2 bg-gray-50 rounded-lg">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-xs font-medium text-gray-800 mt-0.5 capitalize">{value}</div>
    </div>
  );
}
